import { Card, Checkbox, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDPeriodo from "components/MDPeriodo";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import { Form, Formik } from "formik";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDateReqFormat, toDateTime } from "utils/DateUtils";
import { getFile, post, postNoResponse } from "utils/WebService";
import { useUser } from "context/UserContext";
import { TIPO_PERFIL, temTipoPerfil } from "utils/Perfis";
import { MySwal } from "App";
import { ESCURO } from "assets/coresTema";

export default function RecordingsConsult() {
    const { userPerfils } = useUser();
    const isAdminOrSuporte = temTipoPerfil(userPerfils, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);

    const today = new Date();
    const formattedDate = toDateReqFormat(today, 'yyyy-MM-dd');

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        dataInicio: formattedDate,
        dataFim: formattedDate,
    });

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [refresh, setRefresh] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const selectedRecordsRef = useRef({});

    useEffect(() => {
        fetchData({ ...tableState.current });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, dateInit, dateEnd, telefone }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize !== undefined ? pageSize : tableState.current.pageSize,
            pageIndex: pageIndex !== undefined ? pageIndex : tableState.current.pageIndex,
            sorting: sorting !== undefined ? sorting : tableState.current.sorting,
            filtered: filtered !== undefined ? filtered : tableState.current.filtered,
            dataInicio: dateInit !== undefined ? dateInit : tableState.current.dataInicio,
            dataFim: dateEnd !== undefined ? dateEnd : tableState.current.dataFim,
            telefone: telefone !== undefined ? telefone : tableState.current.telefone,
        };

        if (updatedTableState.telefone === undefined) {
             updatedTableState.telefone = "";
        }

        tableState.current = updatedTableState;

        post("/recordings/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            dataInicio: updatedTableState.dataInicio,
            dataFim: updatedTableState.dataFim,
            telefone: updatedTableState.telefone,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data || []);
            setRecordsTotal(recordsTotal || 0);
            setLoadingTable(false);
            // DO NOT reset selection on pagination changes, we want selection to persist!
        }).catch(() => {
            setLoadingTable(false);
            setPages(0);
            setRecordsTotal(0);
            setData([]);
        });

        setRefresh(false);
    }, [refresh]);

    const handleApplyFilter = (values) => {
        setSelectedRows([]);
        selectedRecordsRef.current = {};
        fetchData({
            ...tableState.current, 
            pageIndex: 0, 
            dateInit: values?.dataInicio, 
            dateEnd: values?.dataFim,
            telefone: values?.telefone
        });
    };

    const handleCleanFilter = () => {
        let updatedTableState = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: [],
            dataInicio: formattedDate,
            dataFim: formattedDate,
            telefone: ""
        };
        tableState.current = updatedTableState;
        setSelectedRows([]);
        selectedRecordsRef.current = {};
        fetchData({ ...tableState.current });
    };

    const handleDownload = (id, telefone) => {
        getFile(`/recordings/download/${id}`, `gravacao_${id}_${telefone}.mp3`);
    }

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setLoadingTable(true);
            post("/recordings/list", {
                page: 0,
                pageSize: 99999, // Fetch all for selection
                sorted: tableState.current.sorting,
                filtered: tableState.current.filtered,
                dataInicio: tableState.current.dataInicio,
                dataFim: tableState.current.dataFim,
                telefone: tableState.current.telefone,
            }).then(({ data }) => {
                const allIds = data.map((row) => row.id);
                data.forEach(row => {
                    selectedRecordsRef.current[row.id] = row.telefone;
                });
                setSelectedRows(allIds);
                setLoadingTable(false);
            }).catch(() => setLoadingTable(false));
        } else {
            setSelectedRows([]);
            selectedRecordsRef.current = {};
        }
    };

    const handleSelectRow = (event, row) => {
        if (event.target.checked) {
            setSelectedRows((prev) => [...prev, row.id]);
            selectedRecordsRef.current[row.id] = row.telefone;
        } else {
            setSelectedRows((prev) => prev.filter((rowId) => rowId !== row.id));
            delete selectedRecordsRef.current[row.id];
        }
    };

    const handleDownloadSelected = () => {
        selectedRows.forEach((id, index) => {
            setTimeout(() => {
                const telefone = selectedRecordsRef.current[id] || "sem_telefone";
                handleDownload(id, telefone);
            }, index * 500); // 500ms delay between downloads
        });
    };

    const handleSyncDay = (dataInicio) => {
        MySwal.fire({
            title: `Sincronizar dia ${dataInicio}?`,
            text: "Isso forçará o download de TODAS as gravações deste dia para a pasta na rede. O processo rodará em segundo plano.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, sincronizar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                postNoResponse("/recordings/sync", { data: dataInicio })
                    .then(() => {
                        MySwal.fire(
                            'Iniciado!',
                            'A sincronização foi enviada para processamento em segundo plano. Acompanhe os logs do servidor.',
                            'success'
                        );
                    })
                    .catch(() => {
                        MySwal.fire('Erro', 'Ocorreu um erro ao disparar a sincronização.', 'error');
                    });
            }
        });
    };

    const columns = [
        {
            header: (
                <Checkbox
                    checked={recordsTotal > 0 && selectedRows.length === recordsTotal}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < recordsTotal}
                    onChange={handleSelectAll}
                />
            ),
            accessorKey: "select",
            cell: info => (
                <DefaultCell>
                    <Checkbox
                        checked={selectedRows.includes(info.row.original.id)}
                        onChange={(e) => handleSelectRow(e, info.row.original)}
                    />
                </DefaultCell>
            ),
            isSorted: false,
            width: "5%"
        },
        {
            header: "ID",
            accessorKey: "id",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Telefone",
            accessorKey: "telefone",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Operador",
            accessorKey: "operador",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Tamanho",
            accessorKey: "size",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
            isSorted: false
        },
        {
            header: "Data",
            accessorKey: "date",
            cell: info => <DefaultCell>{toDateTime(info.getValue()) || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Baixar",
            accessorKey: "download",
            isSorted: false,
            cell: info => (
                <Tooltip title="Baixar Arquivo">
                    <IconButton onClick={() => handleDownload(info.row.original.id, info.row.original.telefone)}>
                        <MDTypography variant="h4">
                            <Icon color="inherit" sx={{ marginRight: 1 }}>download</Icon>
                        </MDTypography>
                    </IconButton>
                </Tooltip>
            ),
            width: "10%"
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Formik
                        initialValues={{
                            dataInicio: formattedDate,
                            dataFim: formattedDate,
                            telefone: "",
                        }}
                        onSubmit={(values) => handleApplyFilter(values)}>
                        {({ values, setFieldValue, resetForm }) => (
                            <Form>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Telefone / Arquivo</InputLabel>
                                        <MDInput
                                            size="small"
                                            placeholder="Ex: 9003"
                                            value={values.telefone}
                                            onChange={({ target }) => setFieldValue("telefone", target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleApplyFilter(values);
                                                }
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <MDPeriodo inicio={values.dataInicio} fim={values.dataFim}
                                            onChange={({ inicio, fim }) => { setFieldValue("dataInicio", inicio); setFieldValue("dataFim", fim); }} />
                                    </Grid>
                                    <Grid item xs={12} sm={5} display="flex" justifyContent="flex-end" alignItems="flex-end">
                                        <MDBox pb={0.5}>
                                            <MDButton
                                                size="medium"
                                                sx={{ marginRight: 2 }}
                                                variant="contained"
                                                color="info"
                                                circular={false}
                                                iconOnly={false}
                                                onClick={() => {
                                                    resetForm({
                                                        values: {
                                                            dataInicio: formattedDate,
                                                            dataFim: formattedDate,
                                                            telefone: "",
                                                        },
                                                    });
                                                    handleCleanFilter();
                                                }}
                                            >
                                                Limpar filtro
                                            </MDButton>
                                            <MDButton
                                                size="medium"
                                                variant="contained"
                                                color="info"
                                                circular={false}
                                                iconOnly={false}
                                                type="submit"
                                                sx={{ marginRight: 2 }}
                                            >
                                                Aplicar filtro
                                            </MDButton>
                                            {isAdminOrSuporte && (
                                                <MDButton
                                                    size="medium"
                                                    variant="contained"
                                                    color="warning"
                                                    circular={false}
                                                    iconOnly={false}
                                                    sx={{ marginRight: 2 }}
                                                    onClick={() => handleSyncDay(values.dataInicio)}
                                                >
                                                    Sincronizar Dia
                                                </MDButton>
                                            )}
                                            <MDButton
                                                size="medium"
                                                variant="contained"
                                                color="success"
                                                circular={false}
                                                iconOnly={false}
                                                disabled={selectedRows.length === 0}
                                                onClick={handleDownloadSelected}
                                            >
                                                Baixar Selecionados ({selectedRows.length})
                                            </MDButton>
                                        </MDBox>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={columns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    tableState={tableState} 
                />
            </Grid>
        </Grid>
    );
}
